/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {BUILD_VERSION, METRICS_VERSION} from './BuildVersion';
import Configuration from './Configuration';
import initAnalytics from './AnalyticsScript';
import {initialize} from './AdobeMetricsRuntime';
import initPerformance from './Performance';
import {Level, RecordType} from '@exc/metrics';
import MetricsNetworkIntercept from './MetricsNetworkIntercept';

const LOGGER_NAME = 'exc.metrics-browser-runtime.MetricsBrowserRuntime';

/**
 * Global callback that handles uncaught errors and promise rejections.
 *
 * @param {ErrorEvent} event Holds error information.
 */
function errorEventCallback(event: ErrorEvent): void {
  (global as any).adobeMetrics.log(RecordType.LOG, Level.ERROR, LOGGER_NAME, event.message, 'uncaughtException',
    {sdkVersion: METRICS_VERSION},
    Object.assign({}, event, {
      colno: event.colno,
      error: event.error && Object.assign({}, event.error, {
        message: event.error.message,
        stack: event.error.stack
      }),
      filename: event.filename,
      lineno: event.lineno,
      message: event.message,
      timeStamp: event.timeStamp,
      type: event.type
    })
  );
}

/**
 * Global callback that handles uncaught errors and promise rejections.
 *
 * @param {ErrorEvent | PromiseRejectionEvent} event Holds error information.
 */
function promiseRejectionCallback(event: PromiseRejectionEvent): void {
  (global as any).adobeMetrics.log(RecordType.LOG, Level.ERROR, LOGGER_NAME, event.reason, 'uncaughtPromiseRejection',
    {sdkVersion: METRICS_VERSION},
    Object.assign({}, event, {
      reason: event.reason,
      timeStamp: event.timeStamp,
      type: event.type
    })
  );
}

/**
 * Initialize the MetricsRuntime and the Metrics package. This
 * performs various tasks to add instrumentation to network calls
 * and start background monitors.
 *
 * @param {object} config A congfiguration object for the desired state.
 * @returns {Configuration} The resulting configuration.
 */
function configure(config?: Configuration): Configuration {
  initialize();
  const a = (global as any).adobeMetrics;
  config && a.configure(config);
  if (!a.init.runtime) {
    a.init.runtime = {
      errorEventCallback,
      promiseRejectionCallback
    };
    window.addEventListener('error', errorEventCallback, false);
    window.addEventListener('unhandledrejection', promiseRejectionCallback, false);
  }
  MetricsNetworkIntercept.init(config && config.mode);
  initPerformance();
  initAnalytics(config && config.analytics);
  return a.configure();
}

const MetricsRuntime = {
  configure,
  initialize, // initialize is basically a default configure
  version: BUILD_VERSION
};
export default MetricsRuntime;
